import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    serviceInterests: [],
    urgencyLevel: '',
    preferredContact: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const serviceOptions = [
    { id: 'web-development', label: 'Web Development' },
    { id: 'mobile-apps', label: 'Mobile Applications' },
    { id: 'cloud-services', label: 'Cloud Services' },
    { id: 'cybersecurity', label: 'Cybersecurity' },
    { id: 'data-analytics', label: 'Data Analytics' },
    { id: 'ui-ux-design', label: 'UI/UX Design' },
    { id: 'managed-it', label: 'Managed IT Support' },
    { id: 'digital-transformation', label: 'Digital Transformation' }
  ];

  const urgencyOptions = [
    { value: 'low', label: 'Low Priority - General Inquiry' },
    { value: 'medium', label: 'Medium Priority - Project Discussion' },
    { value: 'high', label: 'High Priority - Urgent Requirement' },
    { value: 'critical', label: 'Critical - Immediate Assistance Needed' }
  ];

  const contactMethodOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'video', label: 'Video Conference' },
    { value: 'meeting', label: 'In-Person Meeting' }
  ];

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.subject?.trim()) {
      newErrors.subject = 'Subject is required';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData?.message?.trim()?.length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    if (!formData?.urgencyLevel) {
      newErrors.urgencyLevel = 'Please select urgency level';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Real-time email validation
    if (field === 'email' && value) {
      if (!validateEmail(value)) {
        setErrors(prev => ({
          ...prev,
          email: 'Please enter a valid email address'
        }));
      }
    }
  };

  const handleServiceInterestChange = (serviceId, checked) => {
    setFormData(prev => ({
      ...prev,
      serviceInterests: checked
        ? [...prev?.serviceInterests, serviceId]
        : prev?.serviceInterests?.filter(id => id !== serviceId)
    }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate API call
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setShowSuccess(true);
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        serviceInterests: [],
        urgencyLevel: '',
        preferredContact: ''
      });
      
      setTimeout(() => {
        setShowSuccess(false);
      }, 5000);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  if (showSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card rounded-xl p-8 border border-border text-center"
      >
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <Icon name="CheckCircle" size={32} className="text-success" />
        </motion.div>
        <h3 className="text-2xl font-bold text-card-foreground mb-2">
          Message Sent Successfully!
        </h3>
        <p className="text-muted-foreground mb-6">
          Thank you for reaching out to DARAJA.LABS. We've received your inquiry and will respond within 2-4 business hours.
        </p>
        <Button
          variant="outline"
          onClick={() => setShowSuccess(false)}
          iconName="ArrowLeft"
          iconPosition="left"
        >
          Send Another Message
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.div
      variants={formVariants}
      initial="hidden"
      animate="visible"
      className="bg-card rounded-xl p-8 border border-border"
    >
      <div className="mb-8">
        <h2 className="text-3xl lg:text-4xl font-bold text-card-foreground mb-4">
          Start Your Project
        </h2>
        <p className="text-muted-foreground text-lg">
          Tell us about your requirements and let's build something amazing together.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Personal Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label="Full Name"
            type="text"
            placeholder="Enter your full name"
            value={formData?.name}
            onChange={(e) => handleInputChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />
          
          <Input
            label="Email Address"
            type="email"
            placeholder="your.email@company.com"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            description="We'll never share your email with anyone else"
            required
          />
        </div>

        {/* Subject */}
        <Input
          label="Subject"
          type="text"
          placeholder="Brief description of your inquiry"
          value={formData?.subject}
          onChange={(e) => handleInputChange('subject', e?.target?.value)}
          error={errors?.subject}
          required
        />

        {/* Service Interests */}
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-3">
            Services of Interest
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {serviceOptions?.map((service) => (
              <Checkbox
                key={service?.id}
                label={service?.label}
                checked={formData?.serviceInterests?.includes(service?.id)}
                onChange={(e) => handleServiceInterestChange(service?.id, e?.target?.checked)}
              />
            ))}
          </div>
        </div>

        {/* Urgency and Contact Method */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Select
            label="Urgency Level"
            options={urgencyOptions}
            value={formData?.urgencyLevel}
            onChange={(value) => handleInputChange('urgencyLevel', value)}
            error={errors?.urgencyLevel}
            placeholder="Select urgency level"
            required
          />

          <Select
            label="Preferred Contact Method"
            options={contactMethodOptions}
            value={formData?.preferredContact}
            onChange={(value) => handleInputChange('preferredContact', value)}
            placeholder="How would you like us to contact you?"
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-card-foreground mb-2">
            Message <span className="text-error">*</span>
          </label>
          <textarea
            placeholder="Tell us about your project requirements, timeline, and any specific needs..."
            value={formData?.message}
            onChange={(e) => handleInputChange('message', e?.target?.value)}
            rows={6}
            className={`w-full px-4 py-3 bg-input border rounded-lg text-card-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent transition-all duration-200 resize-none ${
              errors?.message ? 'border-error' : 'border-border hover:border-accent/50'
            }`}
            required
          />
          {errors?.message && (
            <p className="mt-2 text-sm text-error">{errors?.message}</p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Minimum 10 characters required
          </p>
        </div>

        {/* Submit Button */}
        <div className="pt-4">
          <Button
            type="submit"
            variant="default"
            size="lg"
            loading={isSubmitting}
            disabled={isSubmitting}
            iconName="Send"
            iconPosition="right"
            fullWidth
          >
            {isSubmitting ? 'Sending Message...' : 'Send Message'}
          </Button>
        </div>

        {/* Privacy Notice */}
        <div className="pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground text-center">
            By submitting this form, you agree to our privacy policy and terms of service. 
            We respect your privacy and will never share your information with third parties.
          </p>
        </div>
      </form>
    </motion.div>
  );
};

export default ContactForm;